import { defineStore } from 'pinia'
import { readCookie, writeCookie } from '@outputinc/o-logins'
import { AuthProductName } from '@/types'

const tokenCookieName = import.meta.env.VITE_OUTPUT_TOKEN_COOKIE_NAME
const refreshCookieName = import.meta.env.VITE_OUTPUT_REFRESH_COOKIE_NAME
const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN

/**
 * Authentication store for OWLS (Output Web Login Service).
 *
 * This store is exposed via module federation for use by host applications.
 *
 * Host apps can either:
 * - Use this store directly: `import { useAuthStore } from 'loginService/authStore'`
 * - Implement a compatible store: `import type { AuthStore } from 'loginService/authStore'`
 *
 * @example
 * ```typescript
 * // Host app using OWLS's store directly
 * import { useAuthStore } from 'loginService/authStore'
 * const authStore = useAuthStore()
 * console.log(authStore.isUserAuthenticated)
 * ```
 *
 * @example
 * ```typescript
 * // Host app implementing compatible store
 * import type { AuthStore } from 'loginService/authStore'
 * export const useAuthStore = defineStore('AuthStore', (): AuthStore => {
 *   // ... implement compatible store
 * })
 * ```
 */
export const useAuthStore = defineStore('AuthStore', {
  state: () => ({
    token: readCookie(tokenCookieName, false),
    refresh: readCookie(refreshCookieName, false),
    challengeId: '',
    authProductName: AuthProductName.WEBSITE
  }),
  actions: {
    updateTokens(accessToken: string, refreshToken: string) {
      this.token = accessToken
      this.refresh = refreshToken
    },
    setTokens() {
      this.token = readCookie(tokenCookieName, false)
      this.refresh = readCookie(refreshCookieName, false)
    },
    clearTokens() {
      this.token = ''
      this.refresh = ''
      writeCookie(tokenCookieName, '', cookieDomain, -1)
      writeCookie(refreshCookieName, '', cookieDomain, -1)
    },
    clearRefresh() {
      this.refresh = ''
      writeCookie(refreshCookieName, '', cookieDomain, -1)
    },
    setChallengeId(challengeId: string) {
      this.challengeId = challengeId
    },
    setAuthProductName(name: AuthProductName) {
      if (name.length > 0) {
        this.authProductName = name
      }
    }
  },
  getters: {
    getChallengeId(): string {
      return this.challengeId
    },
    isUserAuthenticated(): boolean {
      return !!this.token
    }
  }
})

/**
 * AuthStore type contract for host applications.
 *
 * Host apps should implement this interface to ensure compatibility with
 * OWLS federated components that depend on AuthStore.
 *
 * @version 1.0.0
 *
 * Changelog:
 * v1.0.0 (2026-01-29) - Initial contract
 *   - State: token, refresh, challengeId, authProductName
 *   - Actions: updateTokens, setTokens, clearTokens, setChallengeId, setAuthProductName
 *   - Optional: clearRefresh (for clearing only refresh token)
 *   - Getters: getChallengeId, isUserAuthenticated
 *
 * @example
 * ```typescript
 * import type { AuthStore } from 'loginService/authStore'
 *
 * export const useAuthStore = defineStore('AuthStore', (): AuthStore => {
 *   // Implementation must satisfy this interface
 *   return {
 *     token, refresh, challengeId, authProductName,
 *     updateTokens, setTokens, clearTokens,
 *     setChallengeId, setAuthProductName,
 *     getChallengeId, isUserAuthenticated
 *     // Optional: clearRefresh
 *   }
 * })
 * ```
 */
export interface AuthStore {
  // State
  token: string | null
  refresh: string | null
  challengeId: string
  authProductName: AuthProductName

  // Actions

  /**
   * Updates both access and refresh tokens in state.
   * Note: Does not write to cookies - that's handled by the login flow.
   * @param accessToken - JWT access token
   * @param refreshToken - JWT refresh token
   */
  updateTokens(accessToken: string, refreshToken: string): void

  /**
   * Reads tokens from cookies and updates state.
   * Used on app initialization and after successful login redirect.
   */
  setTokens(): void

  /**
   * Clears both tokens from state and expires cookies.
   * Should be called on logout or token invalidation.
   */
  clearTokens(): void

  /**
   * Sets the challenge ID for multi-factor authentication flows.
   * @param challengeId - Challenge ID from auth API
   */
  setChallengeId(challengeId: string): void

  /**
   * Sets the product name context for authentication.
   * @param name - Product identifier (e.g., 'website', 'coproducer')
   */
  setAuthProductName(name: AuthProductName): void

  // Optional actions

  /**
   * Clears only the refresh token from state and expires cookie.
   * Optional method - only needed for specific use cases like activation resets.
   * Most apps should use clearTokens() instead.
   */
  clearRefresh?(): void

  // Getters

  /**
   * Returns the current challenge ID.
   * @returns Challenge ID string
   */
  getChallengeId(): string

  /**
   * Checks if user is authenticated by verifying token exists.
   * @returns true if user has valid token, false otherwise
   */
  isUserAuthenticated(): boolean
}
